import { connectionOwnerPaths } from "@/lib/data/requests/connections/owned-connections";
import type { Connection, ConnectionContactPreview, Matter } from "@/lib/types";
import { getConnectionPreviewContact } from "@/lib/utils/connections/item-contact-connections";
import { formatContactsName } from "@/lib/utils/format";
import { getlabel } from "./matter-contact-connection-party-roles";

export interface MatterEmailRecipient {
  contactId: string;
  contactName: string;
  role?: string;
  emails: {
    address: string;
    id: string;
    isPrimary?: boolean;
    label?: string;
  }[];
  contact: ConnectionContactPreview;
}

export const toMatterEmailRecipient = (
  contact: ConnectionContactPreview,
  role?: string,
): MatterEmailRecipient => ({
  contactId: contact.id,
  contactName: formatContactsName(contact) || "Unnamed contact",
  role,
  contact,
  emails: contact.emails.map((email) => ({
    address: email.address ?? "",
    id: email.id,
    isPrimary: email.isPrimary ?? undefined,
    label: email.label ?? undefined,
  })),
});

export const getMatterEmailRecipientContacts = (
  matter: Matter | undefined,
): MatterEmailRecipient[] => {
  const contacts = new Map<string, MatterEmailRecipient>();
  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== "object") return;
    const owner = value as Record<string, unknown>;
    for (const connection of (owner.connections ?? []) as Connection[]) {
      if (
        connection.targetObjectType !== "contact" ||
        (connection.state !== "active" && connection.state !== "scheduled")
      )
        continue;
      const contact = getConnectionPreviewContact(connection);
      if (!contact || contacts.has(contact.id) || !contact.emails.length)
        continue;
      contacts.set(
        contact.id,
        toMatterEmailRecipient(contact, getlabel(connection)),
      );
    }
    for (const [key, child] of Object.entries(owner)) {
      if (connectionOwnerPaths[key]) visit(child);
    }
  };
  visit(matter);
  return [...contacts.values()];
};

export const resolveMatterEmailRecipient = (
  contacts: MatterEmailRecipient[],
  contactId?: string,
  toEmails?: string[],
) => {
  // A shared email address must never substitute another Contact for an
  // explicitly selected identity while that Contact is still loading.
  const selected = contactId
    ? contacts.find((contact) => contact.contactId === contactId)
    : contacts.find((contact) =>
        contact.emails.some((email) => email.address === toEmails?.[0]),
      );
  const email =
    selected?.emails.find((email) => email.address === toEmails?.[0]) ??
    selected?.emails[0];
  return {
    recipientContact: selected?.contact,
    contactId: selected?.contactId ?? "",
    emailId: email?.id ?? "",
  };
};
